import React, { useState } from "react";
import { Box, Typography, Stack } from "@mui/material";
import { DateCalendar, PickersDay, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import FlameIcon from "components/Icons/FlameIcon";
import TooltipLight from "utils/TooltipLight";
import { getPointsTextColor } from "utils/HelperFunctions";
import moment from "moment";

function AchievementCalendar({ pointGoals, workingDays, streakCalendarData }) {
  const { pointTotals, userOutOfOfficeDays } = streakCalendarData;
  const [selectedDate, setSelectedDate] = useState(moment());

  const getDayColor = (date, outsideCurrentMonth) => {
    if (moment().isBefore(date)) {
      return outsideCurrentMonth ? "rgba(0, 0, 0, 0.18)" : "rgba(0, 0, 0, 0.38)";
    }
    const formattedDate = moment(date).format("YYYY-MM-DD");
    const dataForDate = pointTotals.find((data) => data.date === formattedDate);
    const dailyPoints = dataForDate?.totalPoints || 0;
    const color = getPointsTextColor(dailyPoints, 10);
    return outsideCurrentMonth ? `${color}50` : color;
  };

  const getDataForDate = (date) => {
    const formattedDate = moment(date).format("YYYY-MM-DD");
    const dataForDate = pointTotals.find((data) => data.date === formattedDate);
    const dailyPoints = dataForDate?.totalPoints || 0;
    const color = dailyPoints >= 10 ? "#ffce38" : getPointsTextColor(dailyPoints, 10);

    return (
      <Box>
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#888" }}>
          {formattedDate}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ color, fontWeight: 700 }}>{dailyPoints}</Typography>
          <Typography sx={{ fontSize: "0.85rem" }}>total points</Typography>
        </Stack>
      </Box>
    );
  };

  const renderStreakingPointTarget = (isSelected) => (
    <Box className="achievement-calendar-day-wrapper">
      <Box
        className="achievement-calendar-day-inner"
        sx={{
          outline: isSelected ? "2px solid #ff8a1a" : "none",
          backgroundColor: "#ffce38",
        }}
      >
        <FlameIcon sx={{ width: "20px", height: "20px" }} />
      </Box>
    </Box>
  );

  const renderPointTarget = (day, other, isSelected, outsideCurrentMonth) => (
    <Box className="achievement-calendar-day-wrapper">
      <Box
        className="achievement-calendar-day-inner"
        sx={{
          backgroundColor: outsideCurrentMonth ? "#ffce3890" : "#ffce38",
          outline: isSelected ? "2px solid #ff8a1a" : "none",
        }}
      >
        <PickersDay
          className="achievement-calendar-pickers-day"
          {...other}
          outsideCurrentMonth={outsideCurrentMonth}
          disabled
          day={day}
          sx={{
            color: outsideCurrentMonth ? "#ffffffaa !important" : "white !important",
          }}
        />
      </Box>
    </Box>
  );

  const renderNonWorkingDay = (day, other, isSelected, outsideCurrentMonth) => (
    <Box className="achievement-calendar-day-wrapper">
      <Box
        className="achievement-calendar-day-inner"
        sx={{
          backgroundColor: outsideCurrentMonth ? "#e6e6ee70" : "#e6e6ee90",
          outline: isSelected ? "2px solid #ff8a1a" : "none",
        }}
      >
        <PickersDay
          className="achievement-calendar-pickers-day"
          {...other}
          outsideCurrentMonth={outsideCurrentMonth}
          disabled
          day={day}
          sx={{
            color: outsideCurrentMonth
              ? "rgba(0, 0, 0, 0.18) !important"
              : "rgba(0, 0, 0, 0.38) !important",
          }}
        />
      </Box>
    </Box>
  );

  const renderDefaultDay = (day, other, isSelected, outsideCurrentMonth) => (
    <Box className="achievement-calendar-day-wrapper">
      <Box
        className="achievement-calendar-day-inner"
        sx={{
          outline: isSelected ? "2px solid #ff8a1a" : "none",
        }}
      >
        <PickersDay
          className="achievement-calendar-pickers-day"
          {...other}
          disabled
          outsideCurrentMonth={outsideCurrentMonth}
          day={day}
          sx={{
            color: `${getDayColor(day, outsideCurrentMonth)} !important`,
          }}
        />
      </Box>
    </Box>
  );

  const isWorkingDate = (date) => {
    const config = workingDays[date.weekday()] === "1";
    if (!userOutOfOfficeDays.length) return config;

    const ooo = userOutOfOfficeDays.some((item) => item.day === date.format("YYYY-MM-DD"));
    return ooo ? false : config;
  };

  const renderDay = (props) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const pointGoalDay = pointGoals.find((a) => moment(a.achievement_date).isSame(day, "day"));
    const isSelected = selectedDate && moment(day).isSame(selectedDate, "day");
    const isWorkingDay = isWorkingDate(day);
    const dataForDay = moment().isSameOrAfter(day) ? getDataForDate(day) : "";

    let renderedDay;

    if (pointGoalDay) {
      renderedDay = pointGoalDay.streaking
        ? renderStreakingPointTarget(isSelected)
        : renderPointTarget(day, other, isSelected, outsideCurrentMonth);
    } else if (!isWorkingDay) {
      renderedDay = renderNonWorkingDay(day, other, isSelected, outsideCurrentMonth);
    } else {
      renderedDay = renderDefaultDay(day, other, isSelected, outsideCurrentMonth);
    }

    return <TooltipLight title={dataForDay || ""}>{renderedDay}</TooltipLight>;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DateCalendar
        value={selectedDate}
        onChange={(newValue) => setSelectedDate(newValue)}
        slots={{
          day: renderDay,
        }}
        showDaysOutsideCurrentMonth
        fixedWeekNumber={6}
        sx={{
          "& .MuiDayCalendar-slideTransition": {
            minHeight: "230px",
          },
        }}
      />
    </LocalizationProvider>
  );
}

export default AchievementCalendar;
